// Which popup the game shows on its own, and when — the rules, as pure functions.
//
// Popups the player did not ask for (the election announcement, daily rewards,
// the comeback offer, the post-purchase tips) used to decide for themselves when
// to appear, and piled up on top of each other. Now each one only *asks*; these
// rules pick at most one to show:
//
//  1. One at a time. A popup on screen stays until it is closed — a more
//     important one that arrives later waits for it (no flicker, no stacking).
//  2. Nothing while the player is busy: any other map window (a house card, the
//     tutorial, a purchase…) holds the queue, and a shown popup steps aside
//     until that window closes.
//  3. The most important waiting popup goes first: transactional (what just
//     happened to the player) > personal (about them) > event (what is going on
//     in the game). Within a tier, by the fixed order below (unique per tier).
//  4. A short pause after a popup closes or the way clears, and after the map
//     opens (so a popup that arrives a moment later can still win on priority).
//  5. At most SESSION_CAP event popups per app session; the rest wait for the
//     next launch.
//
// Tests: tests/unit/popupRules.test.ts (+ mutation testing, npm run test:mutation:popups).

export type PopupTier = 'transactional' | 'personal' | 'event';

export type PopupId = 'postBuyMessage' | 'comebackOffer' | 'mayorStories' | 'dailyRewards';

/** Lower goes first. */
export const TIER_ORDER: Record<PopupTier, number> = { transactional: 0, personal: 1, event: 2 };

/** Every popup the director knows, its tier and its order inside the tier (lower first). */
export const POPUPS: Record<PopupId, { tier: PopupTier; order: number }> = {
  postBuyMessage: { tier: 'transactional', order: 0 },
  comebackOffer: { tier: 'personal', order: 0 },
  mayorStories: { tier: 'event', order: 0 },
  dailyRewards: { tier: 'event', order: 1 },
};

/** Distinct popups of a tier that may be shown in one app session (no entry = no limit). */
export const SESSION_CAP: Partial<Record<PopupTier, number>> = { event: 2 };

/** Pause after a popup closes or the way clears, before the next one. */
export const GAP_MS = 700;
/** Pause after the map opens before the first popup. */
export const SETTLE_MS = 1200;

export interface DirectorState {
  /** Popups that want to be shown. */
  waiting: PopupId[];
  /** The popup on screen (or stepping aside while the player is busy). */
  current: PopupId | null;
  /** Shown at least once this app session. */
  shown: PopupId[];
  /** Another map window is open. */
  busy: boolean;
  /** Inside a pause (GAP_MS / SETTLE_MS). */
  pausing: boolean;
}

export const initialDirectorState = (): DirectorState => ({
  waiting: [],
  current: null,
  shown: [],
  busy: false,
  pausing: false,
});

/** A popup may still be shown this session (rule 5). */
export function withinSessionCap(
  id: PopupId,
  shown: readonly PopupId[],
  caps: Partial<Record<PopupTier, number>> = SESSION_CAP,
): boolean {
  if (shown.includes(id)) return true;
  const tier = POPUPS[id].tier;
  const cap = caps[tier];
  if (cap === undefined) return true;
  return shown.filter((s) => POPUPS[s].tier === tier).length < cap;
}

/** The waiting popup that should go next (rule 3), or null. */
export function nextInLine(state: DirectorState, caps = SESSION_CAP): PopupId | null {
  const candidates = state.waiting.filter((id) => withinSessionCap(id, state.shown, caps));
  if (candidates.length === 0) return null;
  // Stryker disable next-line EqualityOperator: `waiting` holds each id once and (tier, order) is unique, so the comparison is never 0.
  return candidates.reduce((best, id) => (compareRank(id, best) < 0 ? id : best));
}

/** Tier first, then the order inside the tier. */
const compareRank = (a: PopupId, b: PopupId): number =>
  TIER_ORDER[POPUPS[a].tier] - TIER_ORDER[POPUPS[b].tier] || POPUPS[a].order - POPUPS[b].order;

/** Fill the empty slot if nothing holds it (rules 1, 2, 4). */
export function settle(state: DirectorState): DirectorState {
  if (state.current !== null || state.busy || state.pausing) return state;
  const next = nextInLine(state);
  if (next === null) return state;
  return { ...state, current: next, shown: state.shown.includes(next) ? state.shown : [...state.shown, next] };
}

export function ask(state: DirectorState, id: PopupId): DirectorState {
  if (state.waiting.includes(id)) return state;
  return settle({ ...state, waiting: [...state.waiting, id] });
}

/**
 * The popup no longer wants to be shown (closed, or its reason is gone).
 * `pause` tells the caller to start a GAP_MS pause: the popup was on screen.
 */
export function withdraw(state: DirectorState, id: PopupId): { state: DirectorState; pause: boolean } {
  if (!state.waiting.includes(id)) return { state, pause: false };
  const waiting = state.waiting.filter((w) => w !== id);
  const wasCurrent = state.current === id;
  const next = { ...state, waiting, current: wasCurrent ? null : state.current };
  return wasCurrent ? { state: { ...next, pausing: true }, pause: true } : { state: settle(next), pause: false };
}

/** Another map window opened / closed. `pause` → start a GAP_MS pause before the next popup. */
export function setBusy(state: DirectorState, busy: boolean): { state: DirectorState; pause: boolean } {
  if (state.busy === busy) return { state, pause: false };
  if (busy) return { state: { ...state, busy: true }, pause: false };
  // The way clears: a popup that stepped aside comes straight back; otherwise pause first.
  if (state.current !== null) return { state: { ...state, busy: false }, pause: false };
  return { state: { ...state, busy: false, pausing: true }, pause: true };
}

export function startPause(state: DirectorState): DirectorState {
  return { ...state, pausing: true };
}

export function endPause(state: DirectorState): DirectorState {
  return settle({ ...state, pausing: false });
}

/** What the player sees: the current popup, unless another map window is open. */
export function visiblePopup(state: DirectorState): PopupId | null {
  return state.busy ? null : state.current;
}
